import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, ExternalLink, Loader2, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";

import { GET_BILLING_STATUS } from "@/graphql/queries/students";
import { START_CHECKOUT } from "@/graphql/mutations/students";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

/**
 * What this account has paid for, and how to change it.
 *
 * Nothing here grants anything. Every button hands the browser to Stripe's own
 * page; the subscription and the credits arrive from the webhook when Stripe
 * says the money did. That is why the return from a payment says "kelias
 * sekundes" rather than showing a new balance — the balance is the webhook's to
 * write, and it may land a moment after the redirect.
 */

type Status = {
  subscribed: boolean;
  plan: string | null;
  subscribedUntil: string | null;
  aiCredits: number;
  monthlyAiCredits: number;
  metered: boolean;
  paymentsEnabled: boolean;
  manageUrl: string | null;
  holderId: string;
};

const PLAN_LABEL: Record<string, string> = { monthly: "Mėnesinė", yearly: "Metinė" };

export default function BillingPage() {
  const [params, setParams] = useSearchParams();
  const { data, loading, refetch } = useQuery(GET_BILLING_STATUS, {
    fetchPolicy: "cache-and-network",
  });
  const [startCheckout, { loading: starting }] = useMutation(START_CHECKOUT);
  const [pending, setPending] = useState<string | null>(null);

  const status: Status | null = (data as any)?.billingStatus ?? null;

  // Coming back from Stripe. The webhook usually lands first, but not always —
  // so say what is happening and look again shortly, rather than showing a stale
  // balance and letting them think the payment failed.
  useEffect(() => {
    if (params.get("paid")) {
      toast.success("Ačiū! Mokėjimas gautas — kreditai atsiras per kelias sekundes.");
      const timer = setTimeout(() => refetch(), 3000);
      params.delete("paid");
      params.delete("session_id");
      setParams(params, { replace: true });
      return () => clearTimeout(timer);
    }
    if (params.get("cancelled")) {
      toast("Mokėjimas atšauktas — nieko nenuskaityta.");
      params.delete("cancelled");
      setParams(params, { replace: true });
    }
  }, [params, refetch, setParams]);

  const buy = async (plan: string) => {
    setPending(plan);
    try {
      const result = await startCheckout({ variables: { plan } });
      const payload = (result.data as any)?.startCheckout;
      if (payload?.url) {
        window.location.assign(payload.url);
        return;
      }
      toast.error(payload?.errors?.[0] ?? "Nepavyko pradėti mokėjimo");
    } catch {
      toast.error("Nepavyko pradėti mokėjimo");
    } finally {
      setPending(null);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // No account behind this session — the shared family password signs in as the
  // household rather than as a person, so there is nothing to meter and nothing
  // to buy. Saying so beats rendering an empty page, which is what this did.
  if (!status) {
    return (
      <div className="max-w-3xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Prenumerata ir kreditai</h1>
        </div>
        <Card>
          <CardContent className="space-y-2 py-6 text-sm text-muted-foreground">
            <p>
              Šioje sesijoje prisijungta bendru šeimos slaptažodžiu, todėl atskiros paskyros nėra
              — AI funkcijos čia neribojamos ir nieko pirkti nereikia.
            </p>
            <p>
              Prenumeratą galima tvarkyti prisijungus su savo el. paštu arba Google paskyra.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const used = Math.max(0, status.monthlyAiCredits - status.aiCredits);
  const percentLeft = status.monthlyAiCredits > 0
    ? Math.min(100, Math.round((status.aiCredits / status.monthlyAiCredits) * 100))
    : 0;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Prenumerata ir kreditai</h1>
        <p className="text-muted-foreground">
          AI funkcijos — pokalbiai apie užduotis, ataskaitos, užduočių pritaikymas ir avatarai —
          naudoja kreditus. Kiekvieną mėnesį jie atsinaujina.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-brand-accent" />
              Kreditai
            </CardTitle>
            <CardDescription>
              {status.metered ? (
                <>
                  Liko <strong className="text-foreground tabular-nums">{status.aiCredits}</strong>{" "}
                  iš {status.monthlyAiCredits}. Šį mėnesį panaudota {used}.
                </>
              ) : (
                "Ši paskyra neribojama — AI kreditai iš jos neskaičiuojami."
              )}
            </CardDescription>
          </div>
          {status.subscribed ? (
            <Badge className="bg-primary/10 text-primary hover:bg-primary/10">
              {status.plan ? PLAN_LABEL[status.plan] ?? status.plan : "Aktyvi"}
              {status.subscribedUntil && ` iki ${status.subscribedUntil}`}
            </Badge>
          ) : (
            <Badge variant="secondary">Be prenumeratos</Badge>
          )}
        </CardHeader>
        {status.metered && (
          <CardContent>
            <Progress value={percentLeft} className="h-2" />
            {status.aiCredits === 0 && (
              <p className="mt-3 text-sm text-muted-foreground">
                Kreditai baigėsi. Užduotys, teorija ir sąvokų kortelės veikia kaip įprastai — AI
                pokalbis ir ataskaitos palauks kito mėnesio arba papildymo.
              </p>
            )}
          </CardContent>
        )}
      </Card>

      {!status.paymentsEnabled ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            Mokėjimai kol kas neįjungti. Prenumeratą gali suteikti administratorius.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                {status.subscribed ? "Pakeisti planą" : "Pradėti prenumeratą"}
              </CardTitle>
              <CardDescription>
                Kiekvieną mėnesį — {status.monthlyAiCredits} kreditų. Atšaukti galima bet kada.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button onClick={() => buy("monthly")} disabled={starting}>
                {pending === "monthly" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Mėnesinė
              </Button>
              <Button onClick={() => buy("yearly")} disabled={starting} variant="outline">
                {pending === "yearly" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Metinė
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Papildyti kreditus
              </CardTitle>
              <CardDescription>
                Vienkartinis pirkinys, jei kreditai baigėsi anksčiau laiko. Nepanaudoti papildymo
                kreditai lieka ir kitą mėnesį.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button onClick={() => buy("credits_500")} disabled={starting} variant="outline">
                {pending === "credits_500" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                +500 kreditų
              </Button>
              <Button onClick={() => buy("credits_2000")} disabled={starting} variant="outline">
                {pending === "credits_2000" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                +2000 kreditų
              </Button>
            </CardContent>
          </Card>

          {status.manageUrl && (
            <p className="text-sm text-muted-foreground">
              <a
                href={status.manageUrl}
                className="inline-flex items-center gap-1 underline underline-offset-4"
              >
                Tvarkyti prenumeratą ar kortelę
                <ExternalLink className="h-3.5 w-3.5" />
              </a>{" "}
              — atidaroma Stripe puslapyje.
            </p>
          )}
        </>
      )}
    </div>
  );
}
